import { ExpandLess, ExpandMore, FilterList } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  ClickAwayListener,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useState } from "react";
import { Link } from "react-router-dom";

import { monospaceStyle, panelShadow } from "../../theme";
import { AnalyticsEvidence, AnalyticsFloors } from "../../types/analytics";
import ActiveFilterChips, { ActiveFilter } from "../Filters/ActiveFilterChips";

export interface ScopeBarProps {
  // The selection as chips; none for every product
  filters: ActiveFilter[];
  // Takes one part of the selection away, named by its chip
  onRemove: (id: string) => void;
  // Takes the whole selection away: every product again
  onClearAll: () => void;
  // The totals of the scope, once a read has brought them
  evidence: AnalyticsEvidence | null;
  floors: AnalyticsFloors | null;
  thinScope: boolean;
  // Where Explore lists the products of the selection
  exploreHref: string;
  // What the selection is changed by, shown in the dropdown under the bar
  children: React.ReactNode;
}

const formatCount = (count: number): string => count.toLocaleString("en-US");

const plural = (count: number, noun: string): string =>
  `${formatCount(count)} ${noun}${count === 1 ? "" : "s"}`;

// The scope's totals, as every lens states them: the products only. The
// articles and the single-source share are left out: they said little here.
export const scopeTotalsText = (evidence: AnalyticsEvidence): string =>
  plural(evidence.n, "product");

// What the button of the dropdown is called, with the number of chips
export const filtersButtonName = (active: number): string =>
  active > 0 ? `Filters, ${active} active` : "Filters";

// The stacking order of the dropdown: over the views and their panels,
// under the detail overlay
const DROPDOWN_Z_INDEX = 10;

// What on the bar has a click of its own, and so does not open the
// dropdown: the links, the buttons and the cross of a chip
const OWN_CLICK = "a, button, input, .MuiChip-deleteIcon";

// The scope bar: the selection the page reads over, as chips that take
// their part of it away, the dropdown it is changed in, and the scope's
// totals. The selection is changed here, on the page; only the totals lead
// to Explore, where the products behind them are listed. A click anywhere on
// the bar opens the dropdown, which hangs under it, as wide as it, over the
// view beneath; it is no modal: the chips stay in reach while it is open. A
// second click on the bar, a click beside it or Escape closes it.
const ScopeBar: React.FC<ScopeBarProps> = ({
  filters,
  onRemove,
  onClearAll,
  evidence,
  floors,
  thinScope,
  exploreHref,
  children,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box
        component="section"
        aria-label="Scope"
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === "Escape" && open) {
            event.stopPropagation();
            setOpen(false);
          }
        }}
        sx={{
          position: "relative",
          border: 1,
          borderColor: (theme) =>
            open
              ? alpha(theme.palette.accent.main, 0.55)
              : theme.palette.divider,
          borderRadius: (theme) => `${theme.radius.surface}px`,
          backgroundColor: "background.raised",
          transition: "border-color 120ms",
        }}
      >
        {/* The bar itself: a click anywhere on it opens the dropdown, but on
            what has a click of its own (a chip's cross, a button, the link) */}
        <Box
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            if (!(event.target as HTMLElement).closest(OWN_CLICK)) {
              setOpen((current) => !current);
            }
          }}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
            px: 2,
            py: 1.25,
            cursor: "pointer",
            borderRadius: "inherit",
            transition: "background-color 120ms",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              columnGap: 1.5,
              rowGap: 1,
            }}
          >
            <Typography
              component="span"
              color="text.secondary"
              sx={{
                fontSize: 11,
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Scope
            </Typography>
            {filters.length === 0 ? (
              <Typography variant="h6" component="span">
                Every product
              </Typography>
            ) : (
              <ActiveFilterChips
                filters={filters}
                onRemove={onRemove}
                onClearAll={onClearAll}
              />
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              aria-label={filtersButtonName(filters.length)}
              aria-haspopup="dialog"
              aria-expanded={open}
              onClick={() => setOpen((current) => !current)}
              startIcon={<FilterList fontSize="small" />}
              endIcon={
                open ? (
                  <ExpandLess fontSize="small" />
                ) : (
                  <ExpandMore fontSize="small" />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderColor: "divider",
                backgroundColor: open ? "action.selected" : "transparent",
              }}
            >
              Filters
              {filters.length > 0 && (
                <Box
                  component="span"
                  aria-hidden
                  sx={{ ...monospaceStyle, ml: 0.75, color: "text.secondary" }}
                >
                  {filters.length}
                </Box>
              )}
            </Button>
          </Box>

          <Typography
            role="status"
            variant="caption"
            color="text.secondary"
            sx={monospaceStyle}
          >
            {evidence ? (
              <MuiLink
                component={Link}
                to={exploreHref}
                underline="hover"
                color="inherit"
              >
                {scopeTotalsText(evidence)}
              </MuiLink>
            ) : (
              "Reading the scope…"
            )}
          </Typography>

          {thinScope && (
            <Alert severity="warning" sx={{ py: 0 }}>
              {floors
                ? `Fewer than ${floors.min_scope} products are in this scope.`
                : "Few products are in this scope."}{" "}
              Every number is a hint, not a pattern.
            </Alert>
          )}
        </Box>

        {/* The dropdown: edge to edge with the bar, over the view beneath it */}
        {open && (
          <Box
            role="dialog"
            aria-label="Scope filters"
            sx={{
              position: "absolute",
              top: "100%",
              // The bar's border lies outside what the dropdown is placed in
              left: "-1px",
              right: "-1px",
              zIndex: DROPDOWN_Z_INDEX,
              mt: 0.75,
              p: 2,
              // On a low window the dropdown scrolls rather than run off it
              maxHeight: "calc(100vh - 180px)",
              overflowY: "auto",
              boxSizing: "border-box",
              border: 1,
              borderColor: "divider",
              borderRadius: (theme) => `${theme.radius.surface}px`,
              backgroundColor: "background.raised",
              boxShadow: (theme) => panelShadow(theme),
            }}
          >
            {children}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default ScopeBar;
